import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Gift, getGiftById } from "../../../services/giftApiService";
import { getProduct } from "../../../services/productApiService";
import { CustomCard } from "@repo/ui";
import withAnimation from "../../../hoc/withCardAnimation/withCardAnimation";
import placeholderImage from "../../../assets/placeholders/picture.webp";
import { FaArrowRight } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { withTranslations } from "../../../hoc/withTranslations/withTranslations";

const AnimatedCard = withAnimation(CustomCard);

interface LazyReceivedGiftCardProps {
  giftId: string;
}

const LOCALE = "modules_MyGiftTable_components_LazyReceivedGiftCard";

const LazyReceivedGiftCard: React.FC<LazyReceivedGiftCardProps> = ({
  giftId,
}) => {
  const { t } = useTranslation(LOCALE);
  const [gift, setGift] = useState<Gift | null>(null);
  const [productImages, setProductImages] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const cardRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Fetch gift details and images for its products
  const fetchGiftData = useCallback(async () => {
    try {
      const fetchedGift = await getGiftById(giftId);
      setGift(fetchedGift);

      const images = await Promise.all(
        fetchedGift.Products.map(async (product) => {
          const productData = await getProduct(product.ID);
          return productData.pictures && productData.pictures.length > 0
            ? productData.pictures[0]
            : placeholderImage;
        })
      );
      setProductImages(images);
    } catch (error) {
      console.error("Error fetching gift data:", error);
    } finally {
      setLoading(false);
    }
  }, [giftId]);

  const onIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting) {
        fetchGiftData();
      }
    },
    [fetchGiftData]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(onIntersection, {
      rootMargin: "100px",
      threshold: 0.1,
    });
    if (cardRef.current) {
      observer.observe(cardRef.current);
    }
    return () => observer.disconnect();
  }, [onIntersection]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const renderProductImages = (images: string[]) => {
    if (images.length === 1) {
      return (
        <img
          src={images[0]}
          alt={t("productAlt")}
          className="h-48 w-48 object-cover"
        />
      );
    } else if (images.length === 2) {
      return (
        <div className="grid grid-cols-2 gap-1 h-48 w-48">
          {images.slice(0, 2).map((img, idx) => (
            <img
              key={idx}
              src={img}
              alt={t("productAlt")}
              className="h-24 w-24 object-cover"
            />
          ))}
        </div>
      );
    } else if (images.length === 3) {
      return (
        <div className="grid grid-cols-2 gap-1 h-48 w-48">
          {images.slice(0, 3).map((img, idx) => (
            <img
              key={idx}
              src={img}
              alt={t("productAlt")}
              className="h-24 w-24 object-cover"
            />
          ))}
        </div>
      );
    } else {
      return (
        <div className="relative grid grid-cols-2 gap-1 h-48 w-48">
          {images.slice(0, 3).map((img, idx) => (
            <img
              key={idx}
              src={img}
              alt={t("productAlt")}
              className="h-24 w-24 object-cover"
            />
          ))}
          <div className="flex items-center justify-center h-24 w-24 bg-gray-300 text-black font-bold text-lg rounded-full">
            +{images.length - 3}
          </div>
        </div>
      );
    }
  };

  if (loading) {
    return (
      <div
        ref={cardRef}
        className="h-48 w-full flex items-center justify-center border rounded-s"
      >
        {t("loading")}
      </div>
    );
  }

  if (!gift) {
    return null;
  }

  return (
    <div
      ref={cardRef}
      onClick={() => navigate(`/my-table/gift/${gift.ID}`)}
      className="relative group cursor-pointer transform transition-transform hover:scale-102"
    >
      <AnimatedCard className="rounded-s shadow-none border flex items-center p-4 group-hover:shadow-lg w-full">
        <div className="mr-4">
          {productImages.length > 0 ? (
            renderProductImages(productImages)
          ) : (
            <img
              src={placeholderImage}
              alt={t("loadingAlt")}
              className="h-48 w-48 object-cover"
            />
          )}
        </div>
        <div className="flex flex-col flex-grow">
          <p className="text-gray-600 font-bold mb-2">
            {t("sentBy")}:{" "}
            {gift.IsAnonymous ? t("anonymous") : gift.DonatorName}
          </p>
          <p className="text-gray-500 font-bold mb-2">
            {t("receivedOn")}: {formatDate(gift.GivenDate)}
          </p>
          <p className="text-gray-800 font-bold mb-2">
            {t("quantity")}: {gift.Products[0]?.Quantity}
          </p>
          <p className="text-gray-800 font-bold">
            {t("valueLabel")}: ${gift.Value?.toLocaleString()}
          </p>
        </div>
        <FaArrowRight className="ml-auto text-gray-400 group-hover:text-gray-600 hidden md:block" />
      </AnimatedCard>
    </div>
  );
};

export default withTranslations(LOCALE)(LazyReceivedGiftCard);
